<template>
  <div class="buttons">
    <div class="sidebar">
      <h2>Buttons</h2>
      <form @submit.prevent v-if="metadata">
        <div class="form-group">
          <label>Fill Color</label>
          <div class="input-group" @click="show_fill_color_picker = !show_fill_color_picker">
            <span class="input-group-addon">
              <span class="icon icon-square" :style="'color: ' + fill_color.hex"></span>
            </span>
            <input type="text" ref="fill_color" v-model="fill_color.hex" class="form-control" readonly />
          </div>
          <Sketch v-model="fill_color" :disableAlpha="true" @input="attributeEdited" v-if="show_fill_color_picker" />
        </div>

        <div class="form-group">
          <label>Font Family</label>
          <Multiselect v-model="button_font_family" placeholder="Change Font Family" :options="available_fonts" :searchable="true" label="label" track-by="value" :allow-empty="false" :show-labels="false" @input="attributeEdited">
            <template slot="option" slot-scope="props">
              <div class="option__desc">
                <div class="option__title">{{ props.option.label }}</div>
                <span class="option__small text-muted">{{ props.option.safe ? 'Web Safe' : 'Custom' }}</span>
              </div>
            </template>
          </Multiselect>
        </div>

        <div class="form-group">
          <label>Font Color</label>
          <div class="input-group" @click="show_font_color_picker = !show_font_color_picker">
            <span class="input-group-addon">
              <span class="icon icon-square" :style="'color: ' + font_color.hex"></span>
            </span>
            <input type="text" ref="font_color" v-model="font_color.hex" class="form-control" readonly />
          </div>
          <Sketch v-model="font_color" :disableAlpha="true" @input="attributeEdited" v-if="show_font_color_picker" />
        </div>

        <a class="text-muted" href="#" @click="see_more = !see_more">{{ see_more ? 'Less' : 'More' }} Options <i class="fa fa-caret-down"></i></a>

        <div v-if="see_more" style="width: 100%;">
          <div class="form-group">
            <label>Font Size</label>
            <Multiselect v-model="metadata.button_font_size" placeholder="Font Size" :options="['9px', '10px', '11px', '12px', '13px', '14px', '16px', '18px', '24px', '30px', '36px', '48px', '60px', '72px']" :allow-empty="false" :show-labels="false" @input="attributeEdited" />
          </div>

          <div class="form-group">
            <label>Transform</label>
            <Multiselect v-model="metadata.button_text_transform" placeholder="Text Transform" :options="['None', 'Capitalize', 'Uppercase', 'Lowercase']" :allow-empty="false" :show-labels="false" @input="attributeEdited" />
          </div>

          <div class="form-group">
            <label>Corner Roundness</label>
            <input type="number" v-model.number="border_radius" class="form-control" min="0" @input="attributeEdited" />
          </div>

          <div class="form-group">
            <label>Border Type</label>
            <Multiselect v-model="border_type" placeholder="Border Type" :options="['None', 'Solid', 'Dashed', 'Dotted']" :allow-empty="false" :show-labels="false" @input="attributeEdited" />
          </div>

          <div class="form-group" v-if="border_type !== 'None'">
            <label>Border Width</label>
            <input type="number" v-model="border_width" class="form-control" min="1" @input="attributeEdited" />
          </div>

          <div class="form-group" v-if="border_type !== 'None'">
            <label>Border Color</label>
            <div class="input-group">
              <span class="input-group-addon">
                <span class="icon icon-square" :style="'color: ' + border_color.hex" @click="show_border_color_picker = !show_border_color_picker"></span>
              </span>
              <input type="text" ref="border_color" v-model="border_color.hex" class="form-control" readonly />
            </div>
            <Sketch v-model="border_color" :disableAlpha="true" @input="borderEdited" v-if="show_border_color_picker" />
          </div>

          <div class="form-group">
            <label>Horizontal Padding</label>
            <input type="number" v-model.number="button_inner_padding_horizontal" class="form-control" @input="attributeEdited" />
          </div>

          <div class="form-group">
            <label>Vertical Padding</label>
            <input type="number" v-model.number="button_inner_padding_vertical" class="form-control" @input="attributeEdited" />
          </div>
        </div>

        <button class="xpl-button xpl-button--primary" @click.stop="nextStep">Finish</button>
      </form>
    </div>
    <div class="preview">
      <div class="alert">
        <span class="material-icons">info_outline</span>
        <div>
          <h1>This is a preview of your selections.</h1>
          <p>You are setting the defaults for this account. These defaults can be changed at any time. Also every element in every email can be styled specifically for that email.</p>
        </div>
      </div>

      <div class="branding_preview" v-html="preview_html">
      </div>
    </div>
  </div>
</template>
<script>
  import EventBus from '@/event-bus';
  import * as api from '@/api';
  import Multiselect from 'vue-multiselect';
  import _ from 'lodash';
  import { Sketch } from 'vue-color';
  import Fonts from '../../EditBlocks/fonts.json';

  export default {
    components: {
      Sketch,
      Multiselect,
    },
    data() {
      return {
        see_more: false,
        preview_html: null,
        show_fill_color_picker: false,
        show_font_color_picker: false,
        show_border_color_picker: false,
        font_color: {
          hex: null,
        },
        fill_color: {
          hex: null,
        },
        button_font_family: null,
        border_radius: null,
        border_type: null,
        border_width: null,
        border_color: {
          hex: null,
        },
        button_inner_padding_horizontal: null,
        button_inner_padding_vertical: null,
        metadata: null,
        available_fonts: Fonts,
      };
    },
    created() {
      this.fetchBrandingPreview();
    },
    methods: {
      nextStep() {
        EventBus.$emit('brand_builder.close');
      },
      attributeEdited: _.debounce(function customFunction() {
        const payload = {
          button_background: this.fill_color.hex,
          button_color: this.font_color.hex,
          button_font_family: this.button_font_family.value,
          button_font_size: this.metadata.button_font_size,
          button_text_transform: this.metadata.button_text_transform,
          button_border_radius: `${this.border_radius}px`,
          button_border_type: this.border_type,
          button_border_width: `${this.border_width}px`,
          button_border_color: this.border_color.hex,
          button_inner_padding_horizontal: `${this.button_inner_padding_horizontal}px`,
          button_inner_padding_vertical: `${this.button_inner_padding_vertical}px`,
        };

        api.saveBrandingMetadata(payload).then(() => {
          this.fetchBrandingPreview();
        }).catch((err) => {
          this.$toasted.error(err.message);
        });
      }, 300,
      ),
      fetchBrandingPreview() {
        api.getBrandingPreview().then((response) => {
          this.preview_html = response.preview_html;
          this.metadata = response.metadata;

          this.$nextTick(() => {
            this.fill_color.hex = this.metadata.button_background;
            this.font_color.hex = this.metadata.button_color;
            this.border_color.hex = this.metadata.border_color;
            this.button_font_family = this.available_fonts.filter(f =>
              f.value === response.metadata.button_font_family,
            )[0];
            this.button_font_size = this.metadata.button_font_size;
            this.button_text_transform = this.metadata.button_text_transform;
            this.border_radius = this.metadata.button_border_radius.replace('px', '');
            this.border_type = this.metadata.button_border_type;
            this.border_width = this.metadata.button_border_width.replace('px', '');
            this.button_inner_padding_horizontal = this.metadata.button_inner_padding_horizontal.replace('px', '');
            this.button_inner_padding_vertical = this.metadata.button_inner_padding_vertical.replace('px', '');
          });
        }).catch((err) => {
          this.$toasted.error(err.message);
        });
      },
    },
  };
</script>
<style lang="scss" scoped>
  .vc-sketch {
    position: absolute;
    z-index: 100;
  }

  .buttons {
    display: flex;
    justify-content: flex-start;
    align-items: flex-start;
    width: 100vw;
    height: calc(100vh - 3.5rem);
    padding-left: 20rem;
    background-color: rgb(243, 247, 249);
  }

  .buttons h2 {
    font-size: 1rem;
    font-weight: 700;
    color: #1d364b;
    margin-bottom: .5rem;
  }

  .sidebar {
    position: fixed;
    left: 0;
    min-width: 20rem;
    width: 20rem;
    background-color: white;
    padding: 1rem;
    height: calc(100% - 3.5rem);
    overflow-y: scroll;
  }

  .sidebar form {
    width: 100%;
    display: flex;
    justify-content: flex-start;
    align-items: flex-start;
    flex-direction: column;
  }

  .sidebar form .form-group {
    width: 100%;
  }

  .sidebar form button {
    margin-top: auto;
  }

  .preview {
    flex: 75%;
    padding: 1rem;
    background-color: rgb(243, 247, 249);
    height: 100%;
  }

  .preview .alert {
    display: flex;
    justify-content: flex-start;
    align-items: flex-start;
    border-radius: 0;
    padding-right: 1rem;
    padding-left: 1rem;
    padding-top: 1.5rem;
    padding-bottom: 1.5rem;
    background-color: rgb(255, 255, 255);
  }

  .preview .alert h1 {
    font-size: .875rem;
    line-height: 1.25rem;
    color: rgb(97, 112, 128);
    margin-bottom: 0;
  }

  .preview .alert p {
    font-size: .75rem;
    line-height: 1rem;
    color: rgb(29, 54, 75);
    margin: 0;
  }

  .preview .alert .material-icons {
    color: #AFBCCA;
    margin-right: .5rem;
    font-size: 2rem;
  }

  input[readonly] {
    background-color: white;
  }

  .input-group {
    z-index: 0;
  }

  .input-group input {
    cursor: pointer;
  }

  .input-group-addon {
    padding: .25rem .5rem;
    padding-right: 0;
  }

  .icon-square {
    font-size: 1.5rem;
    cursor: pointer;
  }

  a.text-muted {
    font-size: .875rem;
    color: #848f99;
    margin-bottom: 1rem;
  }

  .branding_preview {
    pointer-events: none;
    margin-left: -1rem;
    margin-right: -1rem;
  }
</style>
